import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorState,
  Input,
  Loading,
  PageHeader,
  Select,
} from '@/components/ui';
import { formatDateTime } from '@/lib/format';

/**
 * Who has asked for an account.
 *
 * This page did not exist. The table has had an `APPROVED` status and a
 * `tenant_id` column since it was created, the acknowledgement email has
 * always told the customer somebody would be in touch, and there was no screen
 * anywhere that listed a single request — so every signup ever made sat in
 * `PENDING` with nobody able to see it.
 */

interface AccessRequest {
  id: string;
  company: string;
  contact_name: string;
  email: string;
  phone: string | null;
  notes: string | null;
  status: string;
  tenant_id: string | null;
  created_at: string;
  decided_at: string | null;
}

const TONES: Record<string, 'amber' | 'green' | 'gray'> = {
  PENDING: 'amber',
  APPROVED: 'green',
  REJECTED: 'gray',
  UNVERIFIED: 'gray',
};

function ApproveForm({
  request,
  onDone,
}: {
  request: AccessRequest;
  onDone: () => void;
}) {
  const queryClient = useQueryClient();
  const [trialDays, setTrialDays] = useState(2);
  const [maxBots, setMaxBots] = useState(2);
  const [concurrent, setConcurrent] = useState(5);

  const approve = useMutation({
    mutationFn: () =>
      api.post(`/admin/access-requests/${request.id}/approve`, {
        trial_days: trialDays,
        max_bots: maxBots,
        max_concurrent_calls: concurrent,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['access-requests'] });
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      onDone();
    },
  });

  return (
    <div className="rounded-md border border-line bg-sunken p-4">
      <div className="grid gap-3 sm:grid-cols-3">
        <label className="text-xs text-ink-600 dark:text-ink-300">
          Trial days
          <Input
            type="number"
            min={0}
            max={90}
            value={trialDays}
            onChange={(e) => setTrialDays(Number(e.target.value))}
            className="mt-1"
          />
        </label>
        <label className="text-xs text-ink-600 dark:text-ink-300">
          Bots
          <Input
            type="number"
            min={0}
            value={maxBots}
            onChange={(e) => setMaxBots(Number(e.target.value))}
            className="mt-1"
          />
        </label>
        <label className="text-xs text-ink-600 dark:text-ink-300">
          Calls at once
          <Input
            type="number"
            min={0}
            value={concurrent}
            onChange={(e) => setConcurrent(Number(e.target.value))}
            className="mt-1"
          />
        </label>
      </div>

      <p className="mt-3 text-xs text-ink-500">
        Creates the client on a {trialDays}-day trial and emails {request.email} a
        link to set their own password. No password is generated or sent. They
        choose their own capacity on first sign-in; these are the limits until
        you raise them.
      </p>

      {approve.error ? (
        <p className="mt-2 text-xs text-red-600 dark:text-red-400">
          {(approve.error as Error).message}
        </p>
      ) : null}

      <div className="mt-3 flex gap-2">
        <Button onClick={() => approve.mutate()} disabled={approve.isPending}>
          {approve.isPending ? 'Creating…' : 'Create the account'}
        </Button>
        <Button variant="secondary" onClick={onDone}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

export default function AccessRequests() {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState('PENDING');
  const [approving, setApproving] = useState<string | null>(null);

  const requests = useQuery({
    queryKey: ['access-requests', status],
    queryFn: () =>
      api.get<{ data: AccessRequest[]; pending: number }>(
        '/admin/access-requests',
        { status },
      ),
  });

  const reject = useMutation({
    mutationFn: (id: string) => api.post(`/admin/access-requests/${id}/reject`, {}),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ['access-requests'] }),
  });

  if (requests.isPending) return <Loading />;
  if (requests.error)
    return <ErrorState error={requests.error} retry={() => requests.refetch()} />;

  const { data, pending } = requests.data;

  return (
    <div className="w-full">
      <PageHeader
        title="Signup requests"
        description={
          pending
            ? `${pending} waiting for a decision.`
            : 'Nobody is waiting for a decision.'
        }
        actions={
          <Select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="w-44"
          >
            <option value="PENDING">Waiting</option>
            <option value="APPROVED">Approved</option>
            <option value="REJECTED">Turned down</option>
            <option value="all">Everything</option>
          </Select>
        }
      />

      {!data.length ? (
        <EmptyState
          title="Nothing here"
          description={
            status === 'PENDING'
              ? 'Requests appear here once somebody signs up and confirms their email address.'
              : 'No requests with that status.'
          }
        />
      ) : (
        <div className="space-y-3">
          {data.map((row) => (
            <Card key={row.id} padded>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-medium text-ink-900 dark:text-ink-50">
                      {row.company}
                    </h3>
                    <Badge tone={TONES[row.status] ?? 'gray'}>{row.status}</Badge>
                  </div>
                  <p className="mt-1 text-sm text-ink-600 dark:text-ink-300">
                    {row.contact_name} · {row.email}
                    {row.phone ? ` · ${row.phone}` : ''}
                  </p>
                  {row.notes ? (
                    <p className="mt-2 whitespace-pre-wrap text-sm text-ink-500">
                      {row.notes}
                    </p>
                  ) : null}
                  <p className="mt-2 text-xs text-ink-400">
                    Asked {formatDateTime(row.created_at)}
                    {row.decided_at
                      ? ` · decided ${formatDateTime(row.decided_at)}`
                      : ''}
                  </p>
                </div>

                {row.status === 'PENDING' && approving !== row.id ? (
                  <div className="flex shrink-0 gap-2">
                    <Button onClick={() => setApproving(row.id)}>Approve</Button>
                    <Button
                      variant="secondary"
                      onClick={() => reject.mutate(row.id)}
                      disabled={reject.isPending}
                    >
                      Turn down
                    </Button>
                  </div>
                ) : null}
              </div>

              {approving === row.id ? (
                <div className="mt-3">
                  <ApproveForm request={row} onDone={() => setApproving(null)} />
                </div>
              ) : null}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
